import React, { useState } from "react";
import {
    Button,
    Grid,
    Paper,
    TableCell,
    TableRow,
    TextField,
    Typography,
} from "@mui/material";

import TableCommon from "../../../components/Common/Table";
import { handleDateTimeDefault } from "../../../helpers/utils";
import { useGetRefundListQuery } from "../../../store/members/membersApis";

const RefundManagerContainer = (props) => {
    const {
        pagination,
        handlePaginationState,
    } = props;

    // ---------------------------------------------------------
    // Search & Date Filter State
    // ---------------------------------------------------------
    const [searchText, setSearchText] = useState(
        pagination?.keywords || ""
    );

    const [fromDate, setFromDate] = useState(
        pagination?.from_date || ""
    );

    const [toDate, setToDate] = useState(
        pagination?.to_date || ""
    );

    // ---------------------------------------------------------
    // Refund List API
    // ---------------------------------------------------------
    const {
        data: refundData,
        isLoading,
        isFetching,
    } = useGetRefundListQuery({
        ...pagination,
        keywords: pagination?.keywords || "",
        from_date: pagination?.from_date || "",
        to_date: pagination?.to_date || "",
    });

    // ---------------------------------------------------------
    // Apply Search / Filter
    // ---------------------------------------------------------
    const handleApplyFilter = () => {
        handlePaginationState({
            ...pagination,
            pageNo: 0,
            keywords: searchText.trim(),
            from_date: fromDate,
            to_date: toDate,
        });
    };

    // ---------------------------------------------------------
    // Clear Search / Filter
    // ---------------------------------------------------------
    const handleClearFilter = () => {
        setSearchText("");
        setFromDate("");
        setToDate("");

        handlePaginationState({
            ...pagination,
            pageNo: 0,
            keywords: "",
            from_date: "",
            to_date: "",
        });
    };

    // ---------------------------------------------------------
    // Table Columns
    // ---------------------------------------------------------
    const columns = [
        {
            title: "Sr. No.",
            sort: false,
            minWidth: 70,
        },
        {
            title: "Member ID",
            field: "member_id",
            sort: true,
            minWidth: 110,
        },
        {
            title: "Member Name",
            field: "member_name",
            sort: true,
            minWidth: 160,
        },
        {
            title: "Refund Amount",
            field: "amount",
            sort: true,
            minWidth: 130,
        },
        {
            title: "Admin Charges",
            field: "charges",
            sort: true,
            minWidth: 120,
        },
        {
            title: "Total Refunded",
            field: "refunded_amount",
            sort: true,
            minWidth: 140,
        },
        {
            title: "Refund Mode",
            field: "refund_mode",
            sort: true,
            minWidth: 130,
        },
        {
            title: "Refund Date",
            field: "refund_date",
            sort: true,
            minWidth: 130,
        },
        {
            title: "Reason",
            field: "reason",
            sort: false,
            minWidth: 220,
        },
        {
            title: "Transaction ID",
            field: "transaction_id",
            sort: false,
            minWidth: 180,
        },
        {
            title: "Wallet Before",
            field: "wallet_balance_before",
            sort: true,
            minWidth: 130,
        },
        {
            title: "Wallet After",
            field: "wallet_balance_after",
            sort: true,
            minWidth: 130,
        },
        {
            title: "Refunded By",
            field: "refunded_by",
            sort: true,
            minWidth: 150,
        },
        {
            title: "Created At",
            field: "createdAt",
            sort: true,
            minWidth: 150,
        },
    ];

    // ---------------------------------------------------------
    // API Data
    // ---------------------------------------------------------
    const refundRecords = refundData?.result || [];
    const totalCount = refundData?.count || 0;

    // ---------------------------------------------------------
    // Table Rows
    // ---------------------------------------------------------
    const tableData =
        !isLoading && refundRecords.length > 0
            ? refundRecords.map((row, index) => (
                  <TableRow key={row._id}>
                      {/* Sr. No. */}
                      <TableCell align="center">
                          {index +
                              1 +
                              (pagination?.pageNo || 0) *
                                  (pagination?.limit || 10)}
                      </TableCell>

                      {/* Member ID */}
                      <TableCell align="center">
                          {row?.member_id || "-"}
                      </TableCell>

                      {/* Member Name */}
                      <TableCell align="center">
                          {row?.member_name || "-"}
                      </TableCell>

                      {/* Refund Amount */}
                      <TableCell align="center">
                          ₹{Number(row?.amount || 0).toFixed(2)}
                      </TableCell>

                      {/* Charges */}
                    <TableCell align="center">
                        ₹{Number(row?.charges || 0).toFixed(2)}
                    </TableCell>

                    {/* Total Refunded */}
                    <TableCell align="center">
                        ₹{Number(row?.refunded_amount || 0).toFixed(2)}
                    </TableCell>

                      {/* Refund Mode */}
                      <TableCell align="center">
                          {row?.refund_mode || "-"}
                      </TableCell>

                      {/* Refund Date */}
                      <TableCell align="center">
                          {row?.refund_date
                              ? handleDateTimeDefault(row.refund_date)
                              : "-"}
                      </TableCell>

                      {/* Reason */}
                      <TableCell align="center">
                          {row?.reason || "-"}
                      </TableCell>

                      {/* Transaction ID */}
                      <TableCell align="center">
                          {row?.transaction_id || "-"}
                      </TableCell>

                      {/* Wallet Before */}
                      <TableCell align="center">
                          ₹
                          {Number(
                              row?.wallet_balance_before || 0
                          ).toFixed(2)}
                      </TableCell>

                      {/* Wallet After */}
                      <TableCell align="center">
                          ₹
                          {Number(
                              row?.wallet_balance_after || 0
                          ).toFixed(2)}
                      </TableCell>

                      {/* Refunded By */}
                      <TableCell align="center">
                          {row?.refunded_by || "-"}
                      </TableCell>

                      {/* Created At */}
                      <TableCell align="center">
                          {row?.createdAt
                              ? handleDateTimeDefault(row.createdAt)
                              : "-"}
                      </TableCell>
                  </TableRow>
              ))
            : (
                  <TableRow
                      sx={{
                          "td, th": {
                              border: 0,
                              padding: "10px",
                          },
                      }}
                  >
                      <TableCell
                          colSpan={14}
                          align="center"
                      >
                          Data not found
                      </TableCell>
                  </TableRow>
              );

    // ---------------------------------------------------------
    // Render
    // ---------------------------------------------------------
    return (
        <Paper sx={{ p: 2 }}>
            <Typography
                variant="h5"
                sx={{
                    mb: 2,
                    fontWeight: 600,
                }}
            >
                Refund Management
            </Typography>

            {/* -------------------------------------------------
                Search & Filter Section
            -------------------------------------------------- */}
            <Grid
                container
                spacing={2}
                alignItems="center"
                sx={{ mb: 2 }}
            >
                {/* Search */}
                <Grid item xs={12} md={5}>
                    <TextField
                        fullWidth
                        size="small"
                        label="Search Refunds"
                        placeholder="Member ID, Name, Transaction ID, Reason..."
                        value={searchText}
                        onChange={(e) =>
                            setSearchText(e.target.value)
                        }
                        onKeyDown={(e) => {
                            if (e.key === "Enter") {
                                handleApplyFilter();
                            }
                        }}
                    />
                </Grid>

                {/* From Date */}
                <Grid item xs={12} sm={6} md={2}>
                    <TextField
                        fullWidth
                        size="small"
                        type="date"
                        label="From Date"
                        InputLabelProps={{
                            shrink: true,
                        }}
                        value={fromDate}
                        onChange={(e) =>
                            setFromDate(e.target.value)
                        }
                    />
                </Grid>

                {/* To Date */}
                <Grid item xs={12} sm={6} md={2}>
                    <TextField
                        fullWidth
                        size="small"
                        type="date"
                        label="To Date"
                        InputLabelProps={{
                            shrink: true,
                        }}
                        value={toDate}
                        onChange={(e) =>
                            setToDate(e.target.value)
                        }
                    />
                </Grid>

                {/* Search Button */}
                <Grid item xs={12} sm={6} md={1.5}>
                    <Button
                        fullWidth
                        variant="contained"
                        onClick={handleApplyFilter}
                    >
                        Search
                    </Button>
                </Grid>

                {/* Clear Button */}
                <Grid item xs={12} sm={6} md={1.5}>
                    <Button
                        fullWidth
                        variant="outlined"
                        onClick={handleClearFilter}
                    >
                        Clear
                    </Button>
                </Grid>
            </Grid>

            {/* -------------------------------------------------
                Refund Table
            -------------------------------------------------- */}
            <TableCommon
                columns={columns}
                tableData={tableData}
                count={totalCount}
                loading={isLoading || isFetching}
                pagination={pagination}
                handlePagination={handlePaginationState}
            />
        </Paper>
    );
};

export default RefundManagerContainer;